import {
  AppBar,
  Button,
  Checkbox,
  Grid,
  InputAdornment,
  InputBase,
  Menu,
  Paper,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Toolbar,
  Typography,
  buttonClasses,
  inputBaseClasses,
  useTheme,
} from '@mui/material';
import usePagination from '@mui/material/usePagination/usePagination';
import {
  CloseCircle,
  DirectionDown,
  DocumentDownload,
  Eye,
  Magnifier,
} from 'asset';
import {
  DeleteDialog,
  FilterMenu,
  HeadTable,
  Layout, PermissionButton,
  TableCaption,
} from 'components';
import PopupCreateOrEditNanny from 'components/Popups/Nanny/PopupCreateOrEditNanny';
import PopupWarningApprove from 'components/Popups/Warning/PopupWarningApprove';
import DeleteAllDialog from 'components/common/DeleteAllDialog';
import { ButtonDelete, ButtonEdit } from 'components/common/table';
import { GetOptionsResponse } from 'components/form-control/Select';
import { drawerWidth } from 'config';
import { NotifyService, getSortItem } from 'helpers';
import { useDateTime } from 'hooks';
import useDebounce from 'hooks/useDebounce';
import {
  DELETE_DIALOG_TYPE,
  FilterValue,
  GET_NANNY_PARAMS_REQUEST,
  NANNY_TYPE,
  STATUS_NANNY,
  TYPE_POPUP,
  colorStatus,
  nameStatus,
  nannyStatuses,
} from 'models';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { nannyApi } from 'services/accounts/nanny';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setNannyListParams } from 'store/reducers/accounts';
import { getNannyListRequest } from 'store/reducers/accounts/actionTypes';
import { filterOptions, headCells } from './data';

const NannyList = () => {
  const theme = useTheme();
  const { formatDateTime } = useDateTime();
  const dispatch = useAppDispatch();
  const { nanny_list } = useAppSelector((state) => state.accountReducer.nanny);
  const { params } = nanny_list;

  const [selected, setSelected] = useState<readonly string[]>([]);
  const [keyword, setKeyword] = useState('');
  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [openCreatePopup, setOpenCreatePopup] = useState(false);
  const [openEditPopup, setOpenEditPopup] = useState(false);
  const [itemAction, setItemAction] = useState<NANNY_TYPE>();
  const [anchorElAction, setAnchorElAction] = useState<null | HTMLElement>(
    null
  );
  const [openDeleteAll, setOpenDeleteAll] = useState<boolean>(false);
  const [openApproveAll, setOpenApproveAll] = useState<boolean>(false);

  const onSetListParams = (params: GET_NANNY_PARAMS_REQUEST | null) => {
    dispatch(setNannyListParams(params));
  };

  useEffect(() => {
    dispatch(getNannyListRequest(params));
  }, [params, dispatch]);

  useEffect(() => {
    setKeyword(params?.keyword ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setOpenDelete = (event: any, data: NANNY_TYPE) => {
    event.stopPropagation();
    setItemAction(data);
    setOpenDeletePopup(true);
  };

  const setOpenEdit = (event: any, data: NANNY_TYPE) => {
    event.stopPropagation();
    setItemAction(data);
    setOpenEditPopup(true);
  };

  const setOpenCreate = () => {
    setOpenCreatePopup(true);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = nanny_list.data.map((n) => n.nannyId);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleOpenBulkAction = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElAction(event.currentTarget);
  };

  const handleClick = (_: any, name: string) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected: readonly string[] = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent) => {
    const pageSize = parseInt(event.target.value, 10);
    onSetListParams({ ...params, pageNum: 1, pageSize });
  };

  const isSelected = (name: string) => selected.indexOf(name) !== -1;

  const { items } = usePagination({
    count: nanny_list.totalPages,
    page: nanny_list.pageNum,
    onChange(_event, page) {
      onSetListParams({ ...params, pageNum: page });
    },
  });

  const onSetKeywordParams = useDebounce((keyword: string) => {
    const _params = { ...params, page: 1 };
    if (keyword) _params.keyword = keyword;
    else delete _params.keyword;
    onSetListParams(_params);
  }, 250);

  const onChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(event.target.value);
    onSetKeywordParams(event.target.value);
  };

  const onClosePopup = (type: TYPE_POPUP) => {
    switch (type) {
      case TYPE_POPUP.DELETE:
        setOpenDeletePopup(false);
        setItemAction(undefined);
        break;
      case TYPE_POPUP.CREATE_OR_EDIT:
        setOpenCreatePopup(false);
        setOpenEditPopup(false);
        setItemAction(undefined);
        break;
      case TYPE_POPUP.DELETE_ALL:
        setOpenDeleteAll(false);
        setAnchorElAction(null);
        break;
      case TYPE_POPUP.APPROVE_ALL:
        setOpenApproveAll(false);
        setAnchorElAction(null);
        break;
    }
  };

  const onReloadList = () => {
    if (!params) return;
    dispatch(getNannyListRequest(params));
  };

  const deleteBulk = async () => {
    const ids: any = selected;
    await nannyApi
      .deleteBulk(ids)
      .then((r) => {
        onReloadList();
        onClosePopup(TYPE_POPUP.DELETE_ALL);
        setSelected([]);
        NotifyService.success(r);
      })
      .catch((e) => NotifyService.error(e))
      .finally(() => {});
  };

  const approveBulk = async () => {
    const ids: any = selected;
    await nannyApi
      .approveBulk(ids)
      .then((r) => {
        onReloadList();
        onClosePopup(TYPE_POPUP.APPROVE_ALL);
        setSelected([]);
        NotifyService.success(r);
      })
      .catch((e) => NotifyService.error(e))
      .finally(() => {});
  };

  const filterValue: FilterValue = useMemo(() => {
    return {
      status: nanny_list.params?.status,
      firstName: nanny_list.params?.firstName,
      lastName: nanny_list.params?.lastName,
      email: nanny_list.params?.email,
    };
  }, [nanny_list.params]);

  const onChangeFilter = (value: FilterValue) => {
    setSelected([]);
    const _params = { ...params };
    delete _params.status;
    delete _params.firstName;
    delete _params.lastName;
    delete _params.email;
    onSetListParams({
      ..._params,
      ...value,
      pageNum: 1,
    });
  };

  const sortItem = useMemo(() => {
    return getSortItem(nanny_list.params?.sort);
  }, [nanny_list.params]);

  const handleRequestSort = (
    _: React.MouseEvent<unknown>,
    property: keyof NANNY_TYPE
  ) => {
    setSelected([]);
    const isAsc = sortItem?.orderBy === property && sortItem?.order === 'asc';
    onSetListParams({
      ...params,
      sort: `${property}-${isAsc ? 'desc' : 'asc'}`,
    });
  };

  const fetchDataOption = async (
    key: string,
    keyword?: string,
    limit: number = 20,
    page: number = 1
  ) => {
    let data: GetOptionsResponse | null = null;
    switch (key) {
      case 'status':
        let optionStatus = nannyStatuses;
        if (keyword)
          optionStatus = optionStatus.filter((t) =>
            t.label?.toLocaleLowerCase().includes(keyword.toLocaleLowerCase())
          );
        data = {
          metadata: {
            page: page,
            limit: limit,
            page_count: optionStatus.length,
            total_pages: 1,
            total_count: optionStatus.length,
          },
          options: optionStatus,
        };
        break;
    }

    return data;
  };

  return (
    <Layout>
      <AppBar
        position="fixed"
        sx={{
          width: `calc(100% - ${drawerWidth}px)`,
          ml: `${drawerWidth}px`,
          backgroundColor: 'white.light',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        }}
        elevation={0}
      >
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Grid>
            <Typography
              sx={{
                fontSize: '20px',
                lineHeight: 1,
                color: 'grey1.light',
              }}
            >
              Accounts / Nanny
            </Typography>
          </Grid>
          <Grid display="flex" gap="8px" flexGrow={1} justifyContent="center">
            <InputBase
              placeholder="Search"
              sx={{
                maxWidth: '369px',
                flexGrow: 1,
                // paddingRight: '9px',
                background: '#F2F4F6',
                border: '1px solid #D9D9D9',
                p: '0px 7px 0px 11px',
                borderRadius: '5px',
                [`& .${inputBaseClasses.input}`]: {
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: '100%',
                  p: 0,
                  height: 'fit-content',
                },
              }}
              onChange={onChangeSearch}
              // autoComplete=""
              value={keyword}
              endAdornment={
                <InputAdornment position="end">
                  <Magnifier />
                </InputAdornment>
              }
            />
            <FilterMenu
              filterOptions={filterOptions}
              filterValue={filterValue}
              onChange={onChangeFilter}
              fetchDataOption={fetchDataOption}
            />
          </Grid>
          <Grid display="flex" gap="8px">
            <Button
              sx={{
                background: '#fff',
                border: `1px solid #226FE3`,
                borderRadius: '3px',
                boxShadow: 'none',

                color: '#226FE3',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '100%',
                textTransform: 'none',

                p: '6px 16px',
                height: '30px',
                alignItems: 'flex-end',
                ':hover': {
                  background: 'rgba(34, 111, 227, 0.08)',
                  border: `1px solid ${theme.palette.secondary.main}`,
                  borderRadius: '3px',
                  boxShadow: 'none',
                },
                [`& .${buttonClasses.endIcon}`]: {
                  mb: '-3px',
                  ml: '3px',
                },
              }}
              variant="contained"
              endIcon={<DirectionDown />}
              onClick={handleOpenBulkAction}
            >
              Bulk Action
            </Button>
            <Menu
              sx={{
                mt: '45px',
                '& .MuiList-padding': {
                  pt: '0px !important',
                  pb: '0px !important',
                },
              }}
              id="menu-appbar"
              anchorEl={anchorElAction}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorElAction)}
              onClose={() => setAnchorElAction(null)}
            >
              <Stack
                sx={{
                  width: '319px',
                  p: '14px 22px 25px 23px',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  ':hover': {
                    backgroundColor: 'white.light',
                  },
                }}
              >
                <Stack spacing="10px">
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '16px',
                      lineHeight: '100%',
                    }}
                  >
                    Bulk Actions
                  </Typography>
                  <Button
                    // startIcon={<Check />}
                    variant="outlined"
                    color="secondary"
                    sx={{
                      p: 0,
                      width: '250px',
                      height: '32px',
                      fontWeight: 600,
                      fontSize: '15px',
                      lineHeight: 1,
                      borderRadius: '10px',
                      '& .MuiButton-startIcon': {
                        mr: '5px',
                      },
                    }}
                    onClick={() => setOpenApproveAll(true)}
                  >
                    Approve Pending Nannies
                  </Button>
                  <Button
                    startIcon={
                      <CloseCircle
                        width={15}
                        heigh={15}
                        color={theme.palette.secondary.main}
                      />
                    }
                    variant="outlined"
                    color="secondary"
                    sx={{
                      p: 0,
                      px: '15px',
                      fontWeight: 600,
                      fontSize: '15px',
                      lineHeight: 1,
                      height: '32px',
                      borderRadius: '10px',
                      '& .MuiButton-startIcon': {
                        mr: '5px',
                      },
                    }}
                    onClick={() => setOpenDeleteAll(true)}
                  >
                    Delete Selected Nannies
                  </Button>
                </Stack>
              </Stack>
            </Menu>
            <PermissionButton
              allowedRoles={['superadmin', 'staff']}
              sx={{
                width: '131px',
                height: 30,
                fontWeight: 600,
                fontSize: '13px',
                border: `1px solid ${theme.palette.secondary.main}`,
                p: '0px',
              }}
              variant="outlined"
              color="secondary"
              endIcon={<DocumentDownload />}
              // onClick={onExportCSV}
            >
              Export to CSV
            </PermissionButton>
            <PermissionButton
              allowedRoles={['superadmin', 'staff']}
              variant="contained"
              color="secondary"
              sx={{
                height: 30,
                width: '72px',
                fontWeight: 600,
                fontSize: '13px',
                lineHeight: '100%',
              }}
              onClick={setOpenCreate}
            >
              Create
            </PermissionButton>
          </Grid>
        </Toolbar>
      </AppBar>
      <Stack spacing="8px">
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: '1px solid #D9D9D9',
            maxHeight: 'calc(100vh - 150px)',
          }}
        >
          <Table
            sx={{ minWidth: 650 }}
            aria-labelledby="tableTitle"
            stickyHeader
          >
            <HeadTable<NANNY_TYPE>
              numSelected={selected.length}
              order={sortItem?.order}
              orderBy={sortItem?.orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={nanny_list.data.length}
              headCells={headCells}
            />
            <TableBody
              sx={{
                height: 'auto !important',
              }}
            >
              {nanny_list.data.map((row, index) => {
                const isItemSelected = isSelected(row.nannyId);
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow
                    hover
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.nannyId}
                    selected={isItemSelected}
                  >
                    <TableCell
                      padding="checkbox"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                      }}
                    >
                      <Checkbox
                        color="primary"
                        checked={isItemSelected}
                        onChange={(e) => handleClick(e, row.nannyId)}
                        inputProps={{
                          'aria-labelledby': labelId,
                        }}
                      />
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {row.firstName || '-'}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {row.lastName || '-'}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {row.email || '-'}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {formatDateTime(
                        row.dateOfBirth,
                        'DD MMM YYYY',
                        'DD-MM-YYYY'
                      )}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {/* {(row.startOfNannyExperience &&
                        calculateExperience(row.startOfNannyExperience)) ||
                        ''} */}
                      {row.yearsOfExperience
                        ? `${row.yearsOfExperience} years`
                        : '-'}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        textTransform="capitalize"
                      >
                        <Grid
                          sx={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            marginRight: '5px',
                            backgroundColor:
                              colorStatus[row.status as STATUS_NANNY],
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: '14px',
                            height: '16px',
                          }}
                        >
                          {nameStatus[row.status as STATUS_NANNY]}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {formatDateTime(row.createdAt, 'DD MMM YYYY HH:mm')}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                      }}
                    >
                      <Stack direction="row" spacing="10px">
                        <Link to={`/accounts/nanny/${row.nannyId}`}>
                          <Button
                            sx={{
                              minWidth: '38px !important',
                              height: '32px',
                              '&.MuiButton-root': {
                                p: '0px !important',
                              },
                            }}
                            variant="contained"
                            color="secondary"
                          >
                            <Eye />
                          </Button>
                        </Link>
                        <ButtonEdit onClick={(e) => setOpenEdit(e, row)} />
                        <ButtonDelete onClick={(e) => setOpenDelete(e, row)} />
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TableCaption
          rowsPerPage={params?.pageSize ?? 0}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          metadata={{
            page: nanny_list.pageNum,
            limit: nanny_list.pageSize,
            page_count: 1,
            total_pages: nanny_list.totalPages,
            total_count: nanny_list.totalSize,
          }}
          items={items}
        />
      </Stack>
      <DeleteDialog
        open={openDeletePopup}
        onClose={() => onClosePopup(TYPE_POPUP.DELETE)}
        subject={itemAction?.nannyId}
        onSuccess={onReloadList}
        type={DELETE_DIALOG_TYPE.NANNY}
        name={itemAction?.firstName}
      />
      {openDeleteAll && (
        <DeleteAllDialog
          open={openDeleteAll}
          onClose={() => onClosePopup(TYPE_POPUP.DELETE_ALL)}
          onClickDelete={deleteBulk}
        />
      )}
      {((openEditPopup && !!itemAction) || openCreatePopup) && (
        <PopupCreateOrEditNanny
          cleanDetail
          subject={itemAction?.nannyId}
          onClose={() => onClosePopup(TYPE_POPUP.CREATE_OR_EDIT)}
          onSuccess={onReloadList}
        />
      )}
      {openApproveAll && (
        <PopupWarningApprove
          open={openApproveAll}
          onClose={() => onClosePopup(TYPE_POPUP.APPROVE_ALL)}
          onClickApprove={approveBulk}
        />
      )}
    </Layout>
  );
};

export default NannyList;
